/**
 * Building blocks of the docs' pages: a page's heading and introduction, sections that can be
 * linked to, paragraphs, lists of points, tables in the app's style, formulas, key figures and
 * panels. A docs page is text first, so these keep one reading column and a calm rhythm, and
 * every block follows the light and dark themes.
 */

import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { IconTile, Kicker } from "../../components/ui.js";

const strong = "font-semibold text-zinc-950 dark:text-white";

/**
 * A docs page: the group it belongs to, its title and its introduction, above its sections. It
 * also names the browser tab after the page, "{title} · Morcast".
 */
export function DocPage({
  group,
  title,
  documentTitle,
  lead,
  children,
}: {
  /** The group of the sidebar the page belongs to, shown above the title. */
  group: string;
  title: string;
  /** The browser tab's name when the heading alone would be unclear there. */
  documentTitle?: string;
  lead: ReactNode;
  children: ReactNode;
}) {
  return (
    <article className="max-w-3xl">
      <title>{`${documentTitle ?? title} · Morcast`}</title>
      <header>
        <Kicker>{group}</Kicker>
        <h1 className="mt-4 font-display text-3xl font-semibold tracking-[-0.03em] text-balance text-zinc-950 sm:text-4xl dark:text-white">
          {title}
        </h1>
        <p className="mt-4 text-lg leading-8 text-zinc-600 dark:text-zinc-400">{lead}</p>
      </header>
      <div className="mt-12 space-y-14">{children}</div>
    </article>
  );
}

/**
 * A section of a docs page, labelled by its heading. Its `id` is its address on the page, and the
 * heading stops below the site's header when a link jumps to it.
 */
export function DocSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id}>
      <h2
        id={id}
        className="scroll-mt-24 font-display text-2xl font-semibold tracking-[-0.02em] text-zinc-950 dark:text-white"
      >
        {title}
      </h2>
      <div className="mt-4 space-y-5">{children}</div>
    </section>
  );
}

/** A heading inside a section. */
export function DocSubheading({ children }: { children: ReactNode }) {
  return (
    <h3 className="pt-3 font-display text-lg font-semibold tracking-[-0.01em] text-zinc-950 dark:text-white">
      {children}
    </h3>
  );
}

/** A paragraph of a docs page. */
export function P({ children }: { children: ReactNode }) {
  return <p className="leading-7 text-zinc-700 dark:text-zinc-300">{children}</p>;
}

/** A point of a list: an optional lead-in set in bold, then its text. */
export interface Point {
  lead?: string;
  text: ReactNode;
}

/** Points as a list with the brand's markers. Each may open with a lead-in in bold. */
export function Points({ items }: { items: Point[] }) {
  return (
    <ul className="list-disc space-y-3 pl-5 leading-7 text-zinc-700 marker:text-brand-500 dark:text-zinc-300 dark:marker:text-brand-400">
      {items.map((item, index) => (
        // The points are fixed text that never reorders.
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed list
        <li key={index} className="pl-1">
          {item.lead && <span className={strong}>{item.lead} </span>}
          {item.text}
        </li>
      ))}
    </ul>
  );
}

/**
 * A table in the app's style. Its first column names each row. Its caption names the table to
 * screen readers. A table of two or three columns wraps its text to fit a phone. One of four or
 * more columns of text keeps a readable width and scrolls sideways inside its frame, so the page
 * itself never does. A table of figures becomes one card per row on a phone (`FigureCards`),
 * where its columns would not fit side by side.
 */
export function DocTable({
  caption,
  head,
  rows,
  numeric = false,
  mono = false,
}: {
  caption: string;
  head: string[];
  /** Each row starts with its name, which also tells the rows apart. */
  rows: [string, ...ReactNode[]][];
  /** Whether the columns after the first hold figures, set to the right in even digits. */
  numeric?: boolean;
  /** Whether the rows' names are code, such as the contract's functions, set in the mono face. */
  mono?: boolean;
}) {
  const cell = "px-3 py-3 align-top sm:px-4";
  const align = numeric ? "text-right tabular-nums" : "";
  // Four columns of sentences would each be a few words wide on a phone.
  const width = head.length >= 4 && !numeric ? "min-w-[40rem]" : "";
  return (
    <>
      {numeric && <FigureCards head={head} rows={rows} />}
      <div
        className={`overflow-x-auto rounded-xl bg-white shadow-[0_1px_2px_rgb(24_24_27/0.04)] ring-1 ring-zinc-900/[0.07] dark:bg-white/[0.02] dark:shadow-none dark:ring-white/[0.08] ${numeric ? "hidden sm:block" : ""}`}
      >
        <table className={`w-full text-left text-sm ${width}`}>
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="border-b border-zinc-900/[0.07] bg-zinc-50/80 dark:border-white/[0.08] dark:bg-white/[0.03]">
              {head.map((label, index) => (
                <th
                  key={label}
                  scope="col"
                  className={`px-3 py-3 text-xs font-semibold text-zinc-600 sm:px-4 dark:text-zinc-300 ${index > 0 ? align : ""}`}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900/[0.06] dark:divide-white/[0.06]">
            {rows.map(([name, ...cells]) => (
              <tr key={name}>
                <th
                  scope="row"
                  className={`${cell} font-medium text-zinc-950 dark:text-white ${mono ? "font-mono text-[0.8125rem]" : ""}`}
                >
                  {name}
                </th>
                {cells.map((value, index) => (
                  <td
                    key={head.at(index + 1)}
                    className={`${cell} leading-6 text-zinc-700 dark:text-zinc-300 ${align}`}
                  >
                    {value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * A table of figures on a phone: one card per row, with the row's name on top and each figure
 * under the name of its column. Five columns of figures do not fit side by side there, and a
 * table that scrolls would hide its last column. From 640 px the table itself is shown instead.
 */
function FigureCards({ head, rows }: { head: string[]; rows: [string, ...ReactNode[]][] }) {
  const label = "text-xs text-zinc-500 dark:text-zinc-400";
  return (
    <div className="space-y-3 sm:hidden">
      {rows.map(([name, ...cells]) => (
        <dl
          key={name}
          className="rounded-xl bg-white p-4 shadow-[0_1px_2px_rgb(24_24_27/0.04)] ring-1 ring-zinc-900/[0.07] dark:bg-white/[0.02] dark:shadow-none dark:ring-white/[0.08]"
        >
          <div className="flex items-baseline justify-between gap-3 border-b border-zinc-900/[0.07] pb-3 dark:border-white/[0.08]">
            <dt className={label}>{head.at(0)}</dt>
            <dd className="font-display font-semibold text-zinc-950 tabular-nums dark:text-white">
              {name}
            </dd>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
            {cells.map((value, index) => (
              <div key={head.at(index + 1)}>
                <dt className={label}>{head.at(index + 1)}</dt>
                <dd className="mt-0.5 font-medium text-zinc-950 tabular-nums dark:text-white">
                  {value}
                </dd>
              </div>
            ))}
          </div>
        </dl>
      ))}
    </div>
  );
}

/**
 * A formula set in a monospace block, dark in both themes as the app's other code is. The label
 * names the figure to screen readers before they read its lines.
 */
export function Formula({ label, children }: { label: string; children: string }) {
  return (
    <figure aria-label={label}>
      <pre className="overflow-x-auto rounded-xl bg-ink-950 px-5 py-4 font-mono text-[0.8125rem] leading-6 text-zinc-100 ring-1 ring-zinc-900/10 dark:bg-black/40 dark:ring-white/10">
        {children}
      </pre>
    </figure>
  );
}

/** Key figures as tiles: the figure set large, then what it is. */
export function KeyFacts({ facts }: { facts: { value: string; label: string }[] }) {
  return (
    <dl className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {facts.map((fact) => (
        <div
          key={fact.label}
          className="relative flex flex-col-reverse justify-end overflow-hidden rounded-2xl bg-white px-5 py-4 shadow-[0_1px_2px_rgb(24_24_27/0.04)] ring-1 ring-zinc-900/[0.07] dark:bg-white/[0.03] dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.04)] dark:ring-white/[0.08]"
        >
          <dt className="mt-1.5 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
            {fact.label}
          </dt>
          <dd className="font-display text-3xl font-semibold tracking-[-0.03em] tabular-nums">
            <span className="text-gradient">{fact.value}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * A panel inside a section: an icon, a title and what belongs under it, such as a table. It is a
 * region named by its title, a level below the section's own heading.
 */
export function DocPanel({
  id,
  icon,
  title,
  description,
  children,
}: {
  id: string;
  icon: LucideIcon;
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      aria-labelledby={id}
      className="rounded-2xl bg-white p-5 shadow-[0_1px_2px_rgb(24_24_27/0.04),0_6px_24px_-12px_rgb(24_24_27/0.12)] ring-1 ring-zinc-900/[0.07] sm:p-6 dark:bg-ink-900/80 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.04),0_1px_2px_rgb(0_0_0/0.3)] dark:ring-white/[0.07]"
    >
      <div className="flex items-start gap-3">
        <IconTile icon={icon} />
        <div className="min-w-0 pt-1">
          <h3
            id={id}
            className="font-display text-lg font-semibold tracking-[-0.015em] text-zinc-950 dark:text-white"
          >
            {title}
          </h3>
          {description && (
            <p className="mt-1 text-sm leading-6 text-zinc-600 dark:text-zinc-400">{description}</p>
          )}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

/** A word or a name set in the monospace face, such as a function of the contract. */
export function Code({ children }: { children: string }) {
  return (
    <code className="rounded-md bg-zinc-900/[0.05] px-1.5 py-0.5 font-mono text-[0.8125em] text-zinc-900 dark:bg-white/[0.08] dark:text-zinc-100">
      {children}
    </code>
  );
}
